import React, { useCallback, useContext, useState } from 'react';
import { useCreateTask } from '~/App/Api/TaskCreation';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import WorkspaceContext from '~/App/Contexts/WorkspaceContext';
import FlashContext from '~/App/Components/Flash/Context';
import useCurrentUser from '~/react-hooks/use-current-user';
import { UserSimple } from '~/types/models';
import { isEmail } from '~/utils/String';
import Context, { TaskCreationContext } from '../TaskCreation/Context';
import { OverlayLoading } from '~/App/Components/OverlayLoading';

export const lottieOptions = {
  loop: true,
  autoplay: true,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export enum TaskCreationAction {
  AddAnother = 'add_another',
  RecordLater = 'record_later',
  RecordNow = 'record_now',
}

export interface TaskCreationWrapperProps {
  children?: React.ReactNode;
  onAddAnotherClick?: () => void;
  onAddAnotherSuccess?: () => void;
  onRecordLaterClick?: () => void;
  onRecordLaterSuccess?: () => void;
  onRecordNowClick?: () => void;
  onRecordNowSuccess?: () => void;
}

export function TaskCreationWrapper({
  children,
  onAddAnotherClick,
  onRecordLaterClick,
  onRecordNowClick,
  onAddAnotherSuccess,
  onRecordLaterSuccess,
  onRecordNowSuccess,
}: TaskCreationWrapperProps) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { createFlash } = useContext(FlashContext);
  const { selectedWorkspace } = useContext(WorkspaceContext);
  const user = useCurrentUser();
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState<UserSimple | undefined>(user);

  const checkFormValidity = useCallback(() => {
    const errors: string[] = [];

    if (email == '' || !isEmail(email)) {
      errors.push('email');
    }

    if (name == '') {
      errors.push('name');
    }

    if (assignee == undefined) {
      errors.push('assignee');
    }

    if (errors.length > 0) {
      setErrorFields(errors);
      return false;
    } else {
      setErrorFields([]);
      return true;
    }
  }, [email, name, assignee, errorFields]);

  const clearForm = () => {
    setEmail('');
    setName('');
    setDescription('');
    setAssignee(user);
    setErrorFields([]);
  };

  const { mutateAsync: createTaskMutation, isLoading: loading } =
    useCreateTask();

  const onSubmit = useCallback(
    (action: TaskCreationAction) => {
      if (!checkFormValidity()) return false;

      onAddAnotherClick?.();
      onRecordLaterClick?.();
      onRecordNowClick?.();

      void createTaskMutation(
        {
          email,
          name,
          assignee_id: assignee?.id as string,
          campaign_id: selectedWorkspace?.uuid,
          note: description,
        },
        {
          onSuccess: (greet) => {
            void queryClient.invalidateQueries('todos');

            createFlash({
              id: 'task-creation-success',
              title: 'Success',
              message: 'Task successfully created!',
            });

            clearForm();

            switch (action) {
              case TaskCreationAction.RecordLater:
                onRecordLaterSuccess?.();
                break;
              case TaskCreationAction.RecordNow:
                onRecordNowSuccess?.();
                history.push(
                  `/home/greet/${greet.id}?action=${TaskCreationAction.RecordNow}`
                );
                break;
              case TaskCreationAction.AddAnother:
                onAddAnotherSuccess?.();
                break;
            }
          },
        }
      );
    },
    [checkFormValidity, errorFields, email, name, description, assignee]
  );

  const context: TaskCreationContext = {
    loading,
    errorFields,
    setErrorFields,
    email,
    setEmail,
    name,
    setName,
    description,
    setDescription,
    assignee,
    setAssignee,
    onSubmit,
    workspace: selectedWorkspace,
  };

  return (
    <Context.Provider value={context}>
      {loading && <OverlayLoading />}
      {children}
    </Context.Provider>
  );
}
